import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import './NewDashboard.css';
import './AdminDashboard.css';
import usuarioService from '../services/UsuarioService';
import ColegioService from '../services/ColegioService';
import ActiveColegioBadge from './common/ActiveColegioBadge';
import UserSessionMenu from './common/UserSessionMenu';
import AdminMenu from './common/menus/AdminMenu';
import SearchableSelect from './common/SearchableSelect';
import { useAuth } from '../contexts/AuthContext';
import { ADMIN_FORM_MENU_ITEMS, navigateAdminMenuItem } from '../utils/adminMenu';

const normalizeList = (list) => (Array.isArray(list) ? list.filter(Boolean) : []);
const getCursoId = (item) => String(item?.idCurso || item?.IdCurso || item?.id || item?.Id || '');
const getValorAnio = (item) => item?.valorAnio ?? item?.ValorAnio ?? '';
const getCursoDivision = (item) => item?.division || item?.Division || '';
const getCursoCiclo = (item) => item?.cicloLectivo || item?.CicloLectivo || '';
const getCursoNombre = (item) => {
  const explicit = item?.nombreCurso || item?.NombreCurso || item?.nombre || item?.Nombre;
  if (explicit) return explicit;
  const valorAnio = getValorAnio(item);
  const division = getCursoDivision(item);
  return valorAnio ? `${valorAnio}° ${division}`.trim() : 'Curso';
};

const AdminEditAlumno = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { alumnoId } = useParams();
  const { user, logout } = useAuth();
  const alumnoState = location.state?.alumno || {};
  const stateColegioId = location.state?.idColegio || null;

  const activeColegio = useMemo(() => ColegioService.getActiveColegio(), []);
  const activeColegioId = useMemo(() => stateColegioId || ColegioService.getActiveColegioId(), [stateColegioId]);
  const activeColegioName = activeColegio?.nombre || activeColegio?.name || activeColegio?.descripcion || 'Colegio';

  const [form, setForm] = useState({
    email: alumnoState.email || alumnoState.Email || '',
    nombre: alumnoState.nombre || alumnoState.Nombre || '',
    apellido: alumnoState.apellido || alumnoState.Apellido || '',
    telefono: alumnoState.telefono || alumnoState.Telefono || '',
    legajo: alumnoState.legajo || alumnoState.Legajo || '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState(''); // success | error | info
  const [cursoOptions, setCursoOptions] = useState([]);
  const [alumnoCursosAsignados, setAlumnoCursosAsignados] = useState([]);
  const [cursosLoading, setCursosLoading] = useState(false);
  const [cursosError, setCursosError] = useState('');
  const [selectedCursoId, setSelectedCursoId] = useState('');
  const [isAssociatingCurso, setIsAssociatingCurso] = useState(false);
  const [cursosMessage, setCursosMessage] = useState('');
  const [cursosMessageType, setCursosMessageType] = useState('');

  const cursoSelectOptions = useMemo(() => {
    const assignedIds = new Set(alumnoCursosAsignados.map(getCursoId).filter(Boolean));
    return cursoOptions
      .filter((item) => {
        const id = getCursoId(item);
        return id && !assignedIds.has(id);
      })
      .map((item) => {
        const curso = getCursoNombre(item);
        const ciclo = getCursoCiclo(item);
        const valorAnio = getValorAnio(item);
        const division = getCursoDivision(item);
        return {
          value: getCursoId(item),
          label: ciclo ? `${curso} · ${ciclo}` : curso,
          secondary: [
            valorAnio && `Año ${valorAnio}`,
            division && `División ${division}`
          ].filter(Boolean).join(' · '),
          searchText: `${curso} ${ciclo} ${valorAnio} ${division}`
        };
      });
  }, [cursoOptions, alumnoCursosAsignados]);

  useEffect(() => {
    if (!alumnoId) {
      navigate('/admin-dashboard', { replace: true });
    }
  }, [alumnoId, navigate]);

  useEffect(() => {
    const fetchAlumnoCursos = async () => {
      if (!activeColegioId || !alumnoId) return;
      setCursosLoading(true);
      setCursosError('');
      try {
        const response = await usuarioService.getAlumnoCursos(alumnoId, activeColegioId);
        setCursoOptions(normalizeList(response.opciones || response.Opciones));
        setAlumnoCursosAsignados(normalizeList(response.asignadas || response.Asignadas));
      } catch (error) {
        setCursoOptions([]);
        setAlumnoCursosAsignados([]);
        setCursosError(error.message || 'No pudimos cargar los cursos del alumno.');
      } finally {
        setCursosLoading(false);
      }
    };

    fetchAlumnoCursos();
  }, [activeColegioId, alumnoId]);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!activeColegioId) {
      setMessage('Selecciona un colegio antes de editar el alumno.');
      setMessageType('error');
      return;
    }
    if (!form.email) {
      setMessage('Completa email para continuar.');
      setMessageType('error');
      return;
    }
    const legajoValue = (form.legajo || '').trim();
    if (!legajoValue) {
      setMessage('Completa legajo para continuar.');
      setMessageType('error');
      return;
    }

    setIsSubmitting(true);
    setMessage('Guardando cambios...');
    setMessageType('info');
    try {
      const displayName = `${form.nombre || ''} ${form.apellido || ''}`.trim() || form.email;
      await usuarioService.updateAlumno(alumnoId, {
        ...form,
        displayName,
        idColegio: activeColegioId,
        legajo: legajoValue
      });
      setMessage('Alumno actualizado correctamente.');
      setMessageType('success');
      setTimeout(() => navigate('/admin-dashboard', { replace: true, state: { tab: 'alumnos' } }), 1000);
    } catch (error) {
      setMessage(error.message || 'No pudimos actualizar el alumno.');
      setMessageType('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAddAlumnoCurso = async () => {
    if (!selectedCursoId || !alumnoId || !activeColegioId) return;
    const curso = cursoOptions.find((item) => getCursoId(item) === selectedCursoId);
    if (!curso) return;
    if (alumnoCursosAsignados.some((item) => getCursoId(item) === selectedCursoId)) return;

    setIsAssociatingCurso(true);
    setCursosMessage('');
    try {
      await usuarioService.assignAlumnoCursos(alumnoId, activeColegioId, [selectedCursoId]);
      setAlumnoCursosAsignados((prev) => [...prev, { ...curso, asignado: true, Asignado: true }]);
      setCursoOptions((prev) => prev.map((item) => (
        getCursoId(item) === selectedCursoId
          ? { ...item, asignado: true, Asignado: true }
          : item
      )));
      setSelectedCursoId('');
      setCursosMessage('Curso asociado al alumno correctamente.');
      setCursosMessageType('success');
    } catch (error) {
      setCursosMessage(error.message || 'No pudimos asociar el curso al alumno.');
      setCursosMessageType('error');
    } finally {
      setIsAssociatingCurso(false);
    }
  };

  const handleRemoveAlumnoCurso = async (cursoId) => {
    if (!cursoId || !alumnoId || !activeColegioId) return;
    setIsAssociatingCurso(true);
    setCursosMessage('');
    try {
      await usuarioService.removeAlumnoCursos(alumnoId, activeColegioId, [cursoId]);
      setAlumnoCursosAsignados((prev) => prev.filter((item) => getCursoId(item) !== cursoId));
      setCursoOptions((prev) => prev.map((item) => (
        getCursoId(item) === cursoId
          ? { ...item, asignado: false, Asignado: false }
          : item
      )));
      setCursosMessage('Curso desasociado del alumno correctamente.');
      setCursosMessageType('success');
    } catch (error) {
      setCursosMessage(error.message || 'No pudimos desasociar el curso del alumno.');
      setCursosMessageType('error');
    } finally {
      setIsAssociatingCurso(false);
    }
  };

  return (
    <div className="new-dashboard admin-mode">
      <aside className="dashboard-sidebar">
        <div className="sidebar-header">
          <div className="sidebar-logo">
            <i className="fas fa-university"></i>
            <span>Admin</span>
          </div>
        </div>
        <AdminMenu
          activeId="alumnos"
          user={user}
          featureFlags={ColegioService.getFeatureFlags()}
          baseItems={ADMIN_FORM_MENU_ITEMS}
          onNavigate={(item) => navigateAdminMenuItem(navigate, item)}
        />
        <div className="sidebar-footer">
          <ActiveColegioBadge />
          <div className="sidebar-user">
            <div className="user-avatar">
              <i className="fas fa-user-shield"></i>
            </div>
            <div className="user-info">
              <span className="user-name">{user?.displayName || user?.name || user?.email || 'Administrador'}</span>
              <span className="user-role">Admin</span>
            </div>
          </div>
        </div>
      </aside>

      <main className="dashboard-main">
        <header className="dashboard-header">
          <div className="header-left">
            <h1 className="page-title">Editar alumno</h1>
            <p className="page-subtitle">Actualiza los datos del alumno asociado al colegio {activeColegioName}.</p>
          </div>
          <div className="header-right">
            <ActiveColegioBadge />
            <UserSessionMenu user={user} onLogout={logout} showStandaloneLogout={false} userFallbackLabel="Administrador" />
          </div>
        </header>

        <div className="dashboard-content" style={{ padding: '2rem', maxWidth: '1200px' }}>
          <div style={{ display: 'flex', gap: '1.5rem', alignItems: 'flex-start', flexWrap: 'wrap' }}>
            <div className="content-card" style={{ flex: '1 1 520px', minWidth: '320px' }}>
            <div className="card-header">
              <div className="card-title">
                <i className="fas fa-user-edit" />
                <span>Datos del alumno</span>
              </div>
            </div>
            <div className="card-content">
              <p style={{ marginTop: 0, color: '#475569' }}>
                Edita los datos del alumno. El legajo se mantiene como referencia académica.
              </p>
              <form onSubmit={handleSubmit} className="prof-create-form">
                <div className="form-field">
                  <label>Email</label>
                  <input
                    type="email"
                    value={form.email}
                    onChange={(e) => handleChange('email', e.target.value)}
                    placeholder="alumno@ejemplo.com"
                  />
                </div>
                <div className="form-grid">
                  <div className="form-field">
                    <label>Nombre</label>
                    <input
                      type="text"
                      value={form.nombre}
                      onChange={(e) => handleChange('nombre', e.target.value)}
                      placeholder="Ana"
                    />
                  </div>
                  <div className="form-field">
                    <label>Apellido</label>
                    <input
                      type="text"
                      value={form.apellido}
                      onChange={(e) => handleChange('apellido', e.target.value)}
                      placeholder="Lopez"
                    />
                  </div>
                </div>
                <div className="form-grid">
                  <div className="form-field">
                    <label>Teléfono</label>
                    <input
                      type="text"
                      value={form.telefono}
                      onChange={(e) => handleChange('telefono', e.target.value)}
                      placeholder="123456"
                    />
                  </div>
                  <div className="form-field">
                    <label>Legajo</label>
                    <input
                      type="text"
                      value={form.legajo}
                      onChange={(e) => handleChange('legajo', e.target.value)}
                      placeholder="B123"
                    />
                  </div>
                </div>

                {message && (
                  <div
                    className="modal-message"
                    style={{
                      marginTop: '0.5rem',
                      borderColor: messageType === 'success' ? '#bbf7d0' : messageType === 'error' ? '#fecaca' : 'var(--gray-200)',
                      background: messageType === 'success' ? '#dcfce7' : messageType === 'error' ? '#fee2e2' : '#f8fafc',
                      color: messageType === 'error' ? '#7f1d1d' : messageType === 'success' ? '#0f5132' : 'var(--gray-800)'
                    }}
                  >
                    {isSubmitting && <i className="fas fa-spinner fa-spin" style={{ marginRight: '0.35rem' }} />}
                    {message}
                  </div>
                )}

                <div className="form-actions">
                  <button type="button" className="btn btn-secondary" onClick={() => navigate('/admin-dashboard')}>
                    Cancelar
                  </button>
                  <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
                    {isSubmitting ? <i className="fas fa-spinner fa-spin" /> : <i className="fas fa-save" />}
                    <span>Guardar cambios</span>
                  </button>
                </div>
              </form>
            </div>
            </div>

            <div className="content-card professor-course-subject-card" style={{ flex: '1 1 360px', minWidth: '300px' }}>
              <div className="card-header">
                <div className="card-title">
                  <i className="fas fa-layer-group" />
                  <span>Cursos del alumno</span>
                </div>
              </div>
              <div className="card-content">
                <p style={{ marginTop: 0, color: '#475569' }}>
                  Asocia al alumno a un curso activo del colegio. El curso se identifica por año, división y ciclo lectivo.
                </p>

                {cursosLoading && <p>Cargando cursos del alumno...</p>}
                {cursosError && <p style={{ color: '#b91c1c' }}>{cursosError}</p>}

                {!cursosLoading && cursoOptions.length > 0 && (
                  <div className="form-field">
                    <label>Curso</label>
                    <div className="professor-course-subject-picker">
                      <SearchableSelect
                        value={selectedCursoId}
                        onChange={setSelectedCursoId}
                        options={cursoSelectOptions}
                        placeholder="Buscar por año, división o ciclo lectivo"
                        emptyText="No hay cursos disponibles para asociar."
                        noResultsText="No encontramos un curso con esa búsqueda."
                        disabled={isAssociatingCurso}
                      />
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={handleAddAlumnoCurso}
                        disabled={!selectedCursoId || isAssociatingCurso}
                      >
                        {isAssociatingCurso ? <i className="fas fa-spinner fa-spin" /> : <i className="fas fa-plus" />}
                        <span>Agregar</span>
                      </button>
                    </div>
                  </div>
                )}

                {!cursosLoading && cursoOptions.length === 0 && !cursosError && (
                  <p style={{ color: '#6b7280', margin: 0 }}>
                    No hay cursos activos para este colegio.
                  </p>
                )}

                <div className="professor-course-subject-list">
                  <p style={{ margin: '0 0 0.5rem 0', color: '#475569', fontWeight: 600 }}>
                    Cursos asociados
                  </p>
                  {alumnoCursosAsignados.length === 0 && (
                    <p style={{ color: '#6b7280', margin: 0 }}>El alumno aún no tiene cursos asociados.</p>
                  )}
                  {alumnoCursosAsignados.map((item) => {
                    const cursoId = getCursoId(item);
                    const ciclo = getCursoCiclo(item);
                    const valorAnio = getValorAnio(item);
                    const division = getCursoDivision(item);
                    return (
                      <div className="professor-course-subject-item" key={cursoId}>
                        <div>
                          <strong>{getCursoNombre(item)}</strong>
                          <span>{[ciclo && `Ciclo ${ciclo}`, valorAnio && `Año ${valorAnio}`, division && `División ${division}`].filter(Boolean).join(' · ')}</span>
                        </div>
                        <button
                          type="button"
                          onClick={() => handleRemoveAlumnoCurso(cursoId)}
                          disabled={isAssociatingCurso}
                          aria-label="Quitar curso"
                        >
                          <i className="fas fa-times" />
                        </button>
                      </div>
                    );
                  })}
                </div>

                {cursosMessage && (
                  <div
                    className="modal-message"
                    style={{
                      marginTop: '0.75rem',
                      borderColor: cursosMessageType === 'success' ? '#bbf7d0' : cursosMessageType === 'error' ? '#fecaca' : 'var(--gray-200)',
                      background: cursosMessageType === 'success' ? '#dcfce7' : cursosMessageType === 'error' ? '#fee2e2' : '#f8fafc',
                      color: cursosMessageType === 'error' ? '#7f1d1d' : cursosMessageType === 'success' ? '#0f5132' : 'var(--gray-800)'
                    }}
                  >
                    {isAssociatingCurso && <i className="fas fa-spinner fa-spin" style={{ marginRight: '0.35rem' }} />}
                    {cursosMessage}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AdminEditAlumno;
